import * as Device from '../core/Device.js';

const MOVE_ACTIONS = ['forward', 'back', 'left', 'right', 'sprint'];
const CONTROLLED_ACTIONS = [...MOVE_ACTIONS, 'jump', 'reload', 'crouch', 'nextWeapon', 'cycleWeapon'];
const JOYSTICK_DEAD_ZONE = 0.18;
const SPRINT_THRESHOLD = 0.8;
const TAP_MAX_DISTANCE = 10;
const TAP_MAX_TIME = 200;

function clamp(value, min, max) {
  if (max < min) return (min + max) * 0.5;
  return Math.max(min, Math.min(max, value));
}

export class TouchControls {
  constructor(ctx) {
    this.ctx = ctx;
    this.supported = Device.prefersTouchControls();
    this.state = 'boot';
    this.touchInput = true;

    this.root = null;
    this.joystickZone = null;
    this.joystick = null;
    this.joystickKnob = null;
    this.lookZone = null;
    this.orientationNotice = null;

    this.pointers = new Map();
    this._offs = [];
    this._activeReported = null;
    this._heldButtons = [new Set(), new Set(), new Set()];
    this._tapShotActive = false;
    this._tapShotFrames = 0;
    this._warnedCapture = false;
  }

  async init() {
    if (!this.supported) {
      this._emitActive(false);
      return;
    }

    this._ensureVirtualInput();
    this._build();

    const events = this.ctx?.events;
    const gameStateEvent = this.ctx?.EVENTS?.GAME_STATE;
    if (events?.on && gameStateEvent) {
      this._offs.push(events.on(gameStateEvent, (payload) => this._onGameState(payload?.state)));
    }

    this._listen(window, 'keydown', (event) => {
      if (!event.repeat && this._isActionKey(event.code)) this._setTouchInput(false);
    }, { capture: true });

    this._listen(window, 'pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.pointerType === 'pen') {
        this._setTouchInput(true);
      }
    }, { capture: true, passive: true });

    this._offs.push(Device.onOrientationChange(() => this._updateOrientation()));
    this._updateOrientation();
    this._syncVisibility();
  }

  update(dt) {
    if (!Number.isFinite(dt) || dt < 0) return;
    if (!this._tapShotActive) return;

    if (this._tapShotFrames >= 1) {
      this._tapShotActive = false;
      this._tapShotFrames = 0;
      this._syncMouseButton(0);
    } else {
      this._tapShotFrames++;
    }
  }

  dispose() {
    this._releaseAll();
    for (const off of this._offs) off();
    this._offs.length = 0;
    this.root?.remove();
    this.root = null;
    this._emitActive(false);
  }

  _build() {
    const uiRoot = document.getElementById('ui-root') || document.body;
    const root = document.createElement('div');
    root.id = 'touch-controls';
    root.className = 'touch-controls touch-capable';
    root.setAttribute('aria-hidden', 'true');

    const lookZone = document.createElement('div');
    lookZone.className = 'touch-look-zone';
    lookZone.setAttribute('aria-label', 'Area de mira');
    root.appendChild(lookZone);
    this.lookZone = lookZone;
    this._bindPointerSurface(lookZone, (event) => this._startLook(event));

    const joystickZone = document.createElement('div');
    joystickZone.className = 'touch-joystick-zone';
    joystickZone.setAttribute('aria-label', 'Controle de movimento');
    root.appendChild(joystickZone);
    this.joystickZone = joystickZone;
    this._bindPointerSurface(joystickZone, (event) => this._startJoystick(event));

    const joystick = document.createElement('div');
    joystick.className = 'touch-joystick';
    joystickZone.appendChild(joystick);
    this.joystick = joystick;

    const knob = document.createElement('div');
    knob.className = 'touch-joystick-knob';
    joystick.appendChild(knob);
    this.joystickKnob = knob;

    const buttons = document.createElement('div');
    buttons.className = 'touch-buttons';
    root.appendChild(buttons);

    buttons.appendChild(this._makeButton('ATIRAR', 'touch-fire', { mouseButton: 0 }));
    buttons.appendChild(this._makeButton('ATIRAR', 'touch-fire touch-fire-left', { mouseButton: 0 }));
    buttons.appendChild(this._makeButton('MIRA', 'touch-aim', { mouseButton: 2 }));
    buttons.appendChild(this._makeButton('PULAR', 'touch-jump', { action: 'jump' }));
    buttons.appendChild(this._makeButton('AGACHAR', 'touch-crouch', { action: 'crouch' }));
    buttons.appendChild(this._makeButton('RECARREGAR', 'touch-reload', { action: 'reload' }));
    buttons.appendChild(// cycleWeapon e nao nextWeapon: no celular nao ha tecla numerica, entao o
      // botao precisa PERCORRER o arsenal - com 7 armas, alternar entre duas
      // deixaria cinco inalcancaveis no aparelho.
      this._makeButton('TROCAR ARMA', 'touch-switch', { action: 'cycleWeapon' }));

    const pause = this._makeButton('PAUSA', 'touch-pause', { command: 'pause' });
    root.appendChild(pause);

    const notice = document.createElement('div');
    notice.className = 'touch-orientation-notice';
    notice.textContent = 'VIRE O APARELHO';
    root.appendChild(notice);
    this.orientationNotice = notice;

    uiRoot.appendChild(root);
    this.root = root;
  }

  _makeButton(label, className, spec) {
    const button = document.createElement('button');
    button.type = 'button';
    button.tabIndex = -1;
    button.className = `touch-button ${className}`;
    button.textContent = label;
    button.setAttribute('aria-label', label);
    this._bindPointerSurface(button, (event) => this._startButton(event, button, spec));
    return button;
  }

  _bindPointerSurface(element, onDown) {
    this._listen(element, 'pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      event.preventDefault();
      event.stopPropagation();
      onDown(event);
    }, { passive: false });
    this._listen(element, 'pointermove', (event) => this._movePointer(event), { passive: false });
    this._listen(element, 'pointerup', (event) => this._endPointer(event, false), { passive: false });
    this._listen(element, 'pointercancel', (event) => this._endPointer(event, true), { passive: false });
    this._listen(element, 'lostpointercapture', (event) => this._endPointer(event, true));
  }

  _listen(target, type, fn, options) {
    target.addEventListener(type, fn, options);
    this._offs.push(() => target.removeEventListener(type, fn, options));
  }

  _startJoystick(event) {
    if (this._hasRole('joystick')) return;
    this._capture(event.currentTarget, event.pointerId);

    const rect = this.joystickZone.getBoundingClientRect();
    const radius = Math.max(50, (this.joystick?.offsetWidth || 120) * 0.5);
    const margin = 8;
    const x = clamp(event.clientX, rect.left + radius + margin, rect.right - radius - margin);
    const y = clamp(event.clientY, rect.top + radius + margin, rect.bottom - radius - margin);

    this.joystick.style.left = `${x}px`;
    this.joystick.style.top = `${y}px`;
    this.joystick.style.bottom = 'auto';
    this.joystick.classList.add('is-floating', 'is-active');

    this.pointers.set(event.pointerId, {
      role: 'joystick',
      element: event.currentTarget,
      centerX: x,
      centerY: y,
      radius
    });
    this._applyJoystick(event.clientX, event.clientY, x, y, radius);
  }

  _startLook(event) {
    if (this._hasRole('look')) return;
    this._capture(event.currentTarget, event.pointerId);
    this.lookZone.classList.add('is-active');
    this.pointers.set(event.pointerId, {
      role: 'look',
      element: event.currentTarget,
      lastX: event.clientX,
      lastY: event.clientY,
      startX: event.clientX,
      startY: event.clientY,
      maxDistanceSq: 0,
      started: performance.now()
    });
  }

  _startButton(event, element, spec) {
    this._capture(element, event.pointerId);
    element.classList.add('is-active');

    const record = { role: 'button', element, spec };
    this.pointers.set(event.pointerId, record);

    if (Number.isInteger(spec.mouseButton)) {
      this._heldButtons[spec.mouseButton].add(event.pointerId);
      this._syncMouseButton(spec.mouseButton, true);
    } else if (spec.action) {
      this._setAction(spec.action, true, true);
    } else if (spec.command === 'pause') {
      this.ctx?.events?.emit?.(this.ctx?.EVENTS?.GAME_STATE, { state: 'paused' });
    }
  }

  _movePointer(event) {
    const record = this.pointers.get(event.pointerId);
    if (!record) return;
    if (event.cancelable) event.preventDefault();

    if (record.role === 'joystick') {
      this._applyJoystick(event.clientX, event.clientY, record.centerX, record.centerY, record.radius);
      return;
    }

    if (record.role === 'look') {
      const dx = event.clientX - record.lastX;
      const dy = event.clientY - record.lastY;
      record.lastX = event.clientX;
      record.lastY = event.clientY;

      const fromStartX = event.clientX - record.startX;
      const fromStartY = event.clientY - record.startY;
      record.maxDistanceSq = Math.max(
        record.maxDistanceSq,
        fromStartX * fromStartX + fromStartY * fromStartY
      );
      this._addLook(dx, dy);
    }
  }

  _endPointer(event, cancelled) {
    const record = this.pointers.get(event.pointerId);
    if (!record) return;
    if (event.cancelable) event.preventDefault();
    this.pointers.delete(event.pointerId);

    if (record.role === 'joystick') {
      this._clearJoystick();
      return;
    }

    if (record.role === 'look') {
      this.lookZone.classList.remove('is-active');
      const elapsed = performance.now() - record.started;
      if (!cancelled
        && record.maxDistanceSq < TAP_MAX_DISTANCE * TAP_MAX_DISTANCE
        && elapsed < TAP_MAX_TIME) {
        this._fireTapShot();
      }
      return;
    }

    if (record.role === 'button') {
      record.element.classList.remove('is-active');
      const spec = record.spec;
      if (Number.isInteger(spec.mouseButton)) {
        this._heldButtons[spec.mouseButton].delete(event.pointerId);
        this._syncMouseButton(spec.mouseButton);
      } else if (spec.action) {
        this._setAction(spec.action, false, false);
      }
    }
  }

  _applyJoystick(pointerX, pointerY, centerX, centerY, radius) {
    let dx = pointerX - centerX;
    let dy = pointerY - centerY;
    const distance = Math.hypot(dx, dy);
    if (distance > radius && distance > 0) {
      const scale = radius / distance;
      dx *= scale;
      dy *= scale;
    }

    this.joystickKnob.style.transform = `translate(-50%, -50%) translate(${dx}px, ${dy}px)`;
    const x = dx / radius;
    const y = dy / radius;
    const magnitude = Math.min(1, Math.hypot(x, y));

    this._setAction('forward', y < -JOYSTICK_DEAD_ZONE);
    this._setAction('back', y > JOYSTICK_DEAD_ZONE);
    this._setAction('left', x < -JOYSTICK_DEAD_ZONE);
    this._setAction('right', x > JOYSTICK_DEAD_ZONE);
    this._setAction('sprint', magnitude >= SPRINT_THRESHOLD);
  }

  _clearJoystick() {
    for (const action of MOVE_ACTIONS) this._setAction(action, false);
    this.joystick?.classList.remove('is-active');
    if (this.joystickKnob) this.joystickKnob.style.transform = 'translate(-50%, -50%)';
  }

  _addLook(x, y) {
    const input = this._ensureVirtualInput();
    if (!input) return;
    input.virtualLook.x += Number.isFinite(x) ? x : 0;
    input.virtualLook.y += Number.isFinite(y) ? y : 0;
  }

  _setAction(action, active, pressed = false) {
    const input = this._ensureVirtualInput();
    if (!input) return;
    const wasActive = input.virtualActions.has(action);
    if (active) {
      input.virtualActions.add(action);
      if (pressed && !wasActive) input.virtualPressed.add(action);
    } else {
      input.virtualActions.delete(action);
    }
  }

  _syncMouseButton(button, forcePressed = false) {
    const input = this._ensureVirtualInput();
    if (!input || button < 0 || button > 2) return;
    const active = this._heldButtons[button].size > 0 || (button === 0 && this._tapShotActive);
    const wasActive = !!input.virtualButtons[button];
    input.virtualButtons[button] = active;
    if (active && (!wasActive || forcePressed)) input.virtualPressedButtons[button] = true;
  }

  _fireTapShot() {
    this._tapShotActive = true;
    this._tapShotFrames = 0;
    this._syncMouseButton(0, true);
  }

  _ensureVirtualInput() {
    const input = this.ctx?.input;
    if (!input) return null;

    if (!(input.virtualActions instanceof Set)) input.virtualActions = new Set();
    if (!(input.virtualPressed instanceof Set)) input.virtualPressed = new Set();
    if (!Array.isArray(input.virtualButtons)) input.virtualButtons = [false, false, false];
    if (!Array.isArray(input.virtualPressedButtons)) input.virtualPressedButtons = [false, false, false];
    while (input.virtualButtons.length < 3) input.virtualButtons.push(false);
    while (input.virtualPressedButtons.length < 3) input.virtualPressedButtons.push(false);
    if (!input.virtualLook || typeof input.virtualLook !== 'object') input.virtualLook = { x: 0, y: 0 };
    if (!Number.isFinite(input.virtualLook.x)) input.virtualLook.x = 0;
    if (!Number.isFinite(input.virtualLook.y)) input.virtualLook.y = 0;
    return input;
  }

  _onGameState(state) {
    if (!state) return;
    const enteringGame = state === 'playing' && this.state !== 'playing';
    this.state = state;
    this._syncVisibility();

    if (enteringGame) {
      this._enterImmersive().catch((err) => {
        console.warn('[TouchControls] modo imersivo recusado:', err);
      });
    }
  }

  async _enterImmersive() {
    await Device.requestFullscreen(document.documentElement);
    await Device.lockLandscape();
    this._updateOrientation();
  }

  _setTouchInput(active) {
    if (!this.supported || this.touchInput === active) return;
    this.touchInput = active;
    this._syncVisibility();
  }

  _syncVisibility() {
    const active = this.supported && this.state === 'playing' && this.touchInput;
    this.root?.classList.toggle('is-playing', this.state === 'playing');
    this.root?.classList.toggle('is-touch-input', this.touchInput);
    this.root?.setAttribute('aria-hidden', active ? 'false' : 'true');
    if (!active) this._releaseAll();
    this._emitActive(active);
  }

  _emitActive(active) {
    if (this._activeReported === active) return;
    this._activeReported = active;
    // O Input precisa saber disso para dispensar o pointer lock em isMouseDown():
    // no celular nao ha pointer lock, e sem esta bandeira o botao de atirar da
    // tela seria lido como "mouse solto" e o jogo ficaria sem tiro.
    const input = this.ctx?.input;
    if (input) {
      input.touchActive = active;
      if (!active) input.clearVirtual?.();
    }
    // Bandeira no <body> para o HUD sair de baixo dos botoes. O HUD nao precisa
    // saber que existe toque: quem reposiciona e o CSS (ver touch.css).
    document.body?.classList?.toggle('is-touch-play', active);
    const type = this.ctx?.EVENTS?.TOUCH_ACTIVE;
    if (type) this.ctx?.events?.emit?.(type, { active });
  }

  _updateOrientation() {
    this.root?.classList.toggle('is-portrait', !Device.isLandscape());
  }

  _isActionKey(code) {
    if (!code) return false;
    const bindings = this.ctx?.input?.bindings;
    if (!bindings || typeof bindings !== 'object') return false;
    for (const codes of Object.values(bindings)) {
      if (Array.isArray(codes) && codes.includes(code)) return true;
    }
    return false;
  }

  _hasRole(role) {
    for (const pointer of this.pointers.values()) {
      if (pointer.role === role) return true;
    }
    return false;
  }

  _capture(element, pointerId) {
    if (typeof element?.setPointerCapture !== 'function') return;
    try {
      element.setPointerCapture(pointerId);
    } catch (err) {
      if (!this._warnedCapture) {
        this._warnedCapture = true;
        console.warn('[TouchControls] captura de ponteiro indisponivel:', err);
      }
    }
  }

  _releaseAll() {
    const input = this._ensureVirtualInput();
    if (input) {
      for (const action of CONTROLLED_ACTIONS) {
        input.virtualActions.delete(action);
        input.virtualPressed.delete(action);
      }
      for (let button = 0; button < 3; button++) {
        input.virtualButtons[button] = false;
        input.virtualPressedButtons[button] = false;
      }
      input.virtualLook.x = 0;
      input.virtualLook.y = 0;
    }

    for (const held of this._heldButtons) held.clear();
    for (const pointer of this.pointers.values()) pointer.element?.classList.remove('is-active');
    this.pointers.clear();
    this._tapShotActive = false;
    this._tapShotFrames = 0;
    this._clearJoystick();
    this.lookZone?.classList.remove('is-active');
  }
}
